import { GlobalStateController } from "bai-react-global-state";
import _ from "lodash";
import React from "react";
import type { User } from "firebase/auth";

import { comments as commentsApi } from "apis/callables";
import { ICommentExtended } from "common/interfaces";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import getLogger, { LogGroup } from "js/core/logger";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import { app } from "js/namespaces";
import Spinner from "js/react/components/Spinner";
import { PusherEventType } from "../../common/constants";

const logger = getLogger(LogGroup.COMMENTS);

export interface CommentsControllerState {
    initialized: boolean;
    initializeError: Error | null;
    presentationId: string;
    presentationLinkId: string | null;
    comments: ICommentExtended[];
}

const initialState: CommentsControllerState = {
    initialized: false,
    initializeError: null,
    presentationId: null,
    presentationLinkId: null,
    comments: []
};

class CommentsController extends GlobalStateController<CommentsControllerState> {
    private _pusherChannel: ExtendedChannel;
    private _pusherChannelUnbind: () => void
    private _runSequentially: SequentialRunner
    private _firebaseUser: User;

    public get initialized() {
        return this._state.initialized;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public get hasUnreadComments() {
        if (!this._state.initialized) {
            return false;
        }

        return this._state.comments.some(comment => comment.authorUid !== this._firebaseUser.uid && (!comment.readBy || !comment.readBy[this._firebaseUser.uid]));
    }

    public async initialize({
        presentationId,
        presentationLinkId,
        byViewer,
        firebaseUser
    }: {
        presentationId: string;
        presentationLinkId?: string;
        byViewer?: boolean;
        firebaseUser: User;
    }) {
        await this._runSequentially(async () => {
            logger.info("[CommentsController] initialize()", { presentationId, presentationLinkId });

            await this._reset(false);

            try {
                this._firebaseUser = firebaseUser;

                this._pusherChannel = await pusher.subscribe(byViewer ? `private-viewer-comments-${presentationLinkId}` : `private-comments-${presentationId}`);
                this._pusherChannelUnbind = this._pusherChannel.bindChunked(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

                const comments = await commentsApi.getComments({ presentationId, presentationLinkId });

                await this._updateState({
                    initialized: true,
                    presentationId,
                    presentationLinkId,
                    comments
                });
            } catch (err) {
                logger.error(err, "[CommentsController] initialize() failed");

                await this._updateState({
                    ..._.cloneDeep(initialState),
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    public reset() {
        return this._reset(true);
    }

    public createComment(slideId: string, comment: Pick<ICommentExtended, "text" | "taggedUsers" | "pendingTaggedUsers" | "byViewer">) {
        return this._runSequentially(async () => {
            const createdComment = await commentsApi.createComment({ slideId, presentationId: this._state.presentationId, presentationLinkId: this._state.presentationLinkId, comment });

            this._updateState(state => ({
                ...state,
                comments: _.cloneDeep([...state.comments, createdComment])
            }));

            return createdComment;
        });
    }

    public updateComment(id: string, slideId: string, update: Pick<ICommentExtended, "text" | "taggedUsers" | "pendingTaggedUsers">) {
        return this._runSequentially(async () => {
            const comment = await commentsApi.updateComment({ id, slideId, presentationId: this._state.presentationId, presentationLinkId: this._state.presentationLinkId, update });

            this._updateState(state => ({
                ...state,
                comments: _.cloneDeep([...state.comments.filter(c => c.id !== id), comment])
            }));
        });
    }

    public deleteComment(id: string, slideId: string) {
        return this._runSequentially(async () => {
            await commentsApi.deleteComment({ id, slideId, presentationId: this._state.presentationId, presentationLinkId: this._state.presentationLinkId });

            this._updateState(state => ({
                ...state,
                comments: _.cloneDeep(state.comments.filter(c => c.id !== id))
            }));
        });
    }

    public markCommentAsRead(id: string, slideId: string) {
        return this._runSequentially(async () => {
            const comment = await commentsApi.markCommentAsRead({ id, slideId, presentationId: this._state.presentationId, presentationLinkId: this._state.presentationLinkId });

            this._updateState(state => ({
                ...state,
                comments: _.cloneDeep([...state.comments.filter(c => c.id !== id), comment])
            }));
        });
    }

    /**
    * Wraps the given component with a state injector that renders the component only if the controller
    * is initialized.
    *
    * !! THIS WRAPPER WILL *NOT* INITIALIZE THE CONTROLLER IF IT'S NOT ALREADY INITIALIZED !!
    */
    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    private _onPusherEvent = (payload: { added: ICommentExtended[], removed: ICommentExtended[], updated: ICommentExtended[] }) => {
        this._runSequentially(async () => {
            const { added, removed, updated } = payload;

            this._updateState(state => {
                const existingComments = state.comments;

                const comments: ICommentExtended[] = [];

                const allCommentIds = new Set([...existingComments.map(c => c.id), ...added.map(c => c.id), ...updated.map(c => c.id), ...removed.map(c => c.id)]);
                allCommentIds.forEach(id => {
                    const removedComment = removed.find(c => c.id === id);
                    if (removedComment) {
                        return;
                    }

                    const addedOrUpdatedComment = _.cloneDeep(added.find(c => c.id === id) ?? updated.find(c => c.id === id));
                    const existingComment = _.cloneDeep(existingComments.find(c => c.id === id));

                    if (!existingComment) {
                        comments.push(addedOrUpdatedComment);
                        return;
                    }

                    if (!addedOrUpdatedComment) {
                        comments.push(existingComment);
                        return;
                    }

                    if (!addedOrUpdatedComment.modifiedAt || !existingComment.modifiedAt || (addedOrUpdatedComment.modifiedAt > existingComment.modifiedAt)) {
                        comments.push(addedOrUpdatedComment);
                    } else {
                        comments.push(existingComment);
                    }
                });

                return {
                    ...state,
                    comments
                };
            });
        });
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[CommentsController] reset()");

            this._firebaseUser = null;

            if (this._pusherChannel) {
                this._pusherChannelUnbind();
                if (!this._pusherChannel.isInUse) {
                    pusher.unsubscribe(this._pusherChannel.name);
                }
                this._pusherChannel = null;
                this._pusherChannelUnbind = null;
            }

            await this._updateState(_.cloneDeep(initialState));
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }
}

const commentsController = new CommentsController();

// for lazy import and debug
app.commentsController = commentsController;

export default commentsController;
